import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useFirebaseAuth } from '../services/firebaseAuthService';
import { useAuth } from '../context/AuthContext';

// Helper function to trigger Google Ads conversion event (safe-guarded)
const gtagSendEvent = (url) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const callback = () => {
    if (typeof url === 'string') {
      window.location = url;
    }
  };
  window.gtag('event', 'conversion_event_signup', {
    event_callback: callback,
    event_timeout: 2000,
    // <event_parameters>
  });
};

export default function Register() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signUpWithEmail, signInWithGoogle } = useFirebaseAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setFormData((prevData) => ({ ...prevData, email: emailParam }));
    }
  }, [location]);

  // Handle auth redirects
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={location.state?.from || '/'} replace />;
  }

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const { name, email, password } = formData;

    if (!name || !email || !password) {
      setMessage({ type: 'error', text: 'All fields are required.' });
      return false;
    }

    if (!validateEmail(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return false;
    }

    if (!validatePassword(password)) {
      setMessage({
        type: 'error',
        text: 'Password must be 8-16 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character.',
      });
      return false;
    }

    return true;
  };

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const validatePassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,16}$/.test(password);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!validateForm()) return;

    setIsSubmitting(true);
    const { name, email, password } = formData;

    try {
      await signUpWithEmail(name, '', email, password);
      setMessage({ 
        type: 'success', 
        text: 'Account created! Please check your email to verify your account. You will not be able to log in until you verify your email address.' 
      });
      // NEW: Fire Google Ads conversion event
      gtagSendEvent();
      setFormData({ name: '', email: '', password: '' });
    } catch (err) {
      console.error('Error:', err.message);
      setMessage({ type: 'error', text: 'An error occurred while signing up. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      // NEW: Fire Google Ads conversion event
      gtagSendEvent();
      setMessage({ type: 'success', text: 'Signed in with Google successfully!' });
    } catch (err) {
      console.error('Error:', err.message);
      setMessage({ type: 'error', text: 'An error occurred while signing in with Google. Please try again.' });
    }
  };

  return (
    <section className="bg-white mt-25">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-3xl overflow-hidden">
          <div className="flex justify-center">
            <div className="w-full xl:w-1/2">
              <div className="px-8 md:px-16 pt-16 pb-20">
                <form onSubmit={handleSignUp} noValidate>
                  <span className="inline-block text-neutral-600 text-xl font-semibold tracking-tight mb-4">
                    Account
                  </span>
                  <h1 className="text-4xl font-semibold tracking-tight mb-6 font-heading">
                    Get Started for Free
                  </h1>
                  <button
                    type='button'
                    className='flex justify-center items-center text-center w-full mb-6 p-4 font-semibold tracking-tight border border-neutral-900 bg-white hover:border-blue-500 hover:text-blue-500 focus:border-blue-600 focus:text-blue-600 rounded-lg focus:ring-4 focus:ring-neutral-400 transition duration-200'
                    aria-label='Sign in with Google'
                    onClick={handleGoogleSignIn}
                  >
                    <img
                      className='mr-2 h-6'
                      src='/assets/auth/google.svg' 
                      alt='Google logo'
                    />
                    <span>Continue with Google</span>
                  </button>
                  <p className='text-center text-neutral-600 font-medium tracking-tight mb-2'>
                    OR
                  </p>
                  {message.text && (
                    <p
                      className={`mb-4 ${
                        message.type === 'error' ? 'text-red-500' : 'text-green-500'
                      }`}
                    >
                      {message.text}
                    </p>
                  )}
                  <div className="flex flex-wrap -m-3 mb-3">
                    <div className="w-full p-3">
                      <label className="block mb-1 text-neutral-600 font-medium" htmlFor="name">
                        Name
                      </label>
                      <input
                        className="w-full px-8 py-5 outline-none rounded-lg border placeholder-neutral-300 font-medium focus:ring-4 transition duration-200"
                        id="name"
                        type="text"
                        placeholder="e.g., John Doe"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="w-full p-3">
                      <label className="block mb-1 text-neutral-600 font-medium" htmlFor="email">
                        Email
                      </label>
                      <input
                        className="w-full px-8 py-5 outline-none rounded-lg border placeholder-neutral-300 font-medium focus:ring-4 transition duration-200"
                        id="email"
                        type="email"
                        placeholder="e.g., john@example.com"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="w-full p-3">
                      <label className="block mb-1 text-neutral-600 font-medium" htmlFor="password">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          className="w-full pl-8 pr-12 py-5 outline-none rounded-lg border placeholder-neutral-300 font-medium focus:ring-4 transition duration-200"
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                        />
                        <button
                          type="button"
                          className="absolute top-1/2 right-4 -translate-y-1/2 flex items-center text-neutral-600 hover:text-neutral-900"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M2 2l20 20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M10.73 5.08A9.77 9.77 0 0 1 12 5c7 0 10 7 10 7a17.69 17.69 0 0 1-2.22 3.34" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M6.18 6.18C3.16 8.24 2 12 2 12a17.77 17.77 0 0 0 5.09 6.21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M1 12S5 5 12 5s11 7 11 7-4 7-11 7S1 12 1 12Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="w-full p-3">
                      <button
                        className="flex justify-center items-center text-center h-full w-full p-5 font-semibold text-lg text-white bg-neutral-900 hover:bg-neutral-800 focus:bg-neutral-800 rounded-lg focus:ring-4 focus:ring-neutral-400 transition duration-200"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Processing...' : 'Sign Up Now'}
                      </button>
                    </div>
                  </div>
                  <a
                    className="inline-block text-neutral-600 text-sm font-medium hover:text-neutral-800 tracking-tight transition duration-200"
                    href="login"
                  >
                    Already have an account? Log in
                  </a>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}